import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Alert,
  Chip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Warning as WarningIcon, VpnKey as KeyIcon } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "@/store";
import { fetchImages } from "@/store/imagesSlice";
import { useLocalKeys } from "@hooks/useLocalKeys";
import { useNotification } from "@context/NotificationContext";
import { routes } from "@/constants/routes";

export default function Quarantine() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const { items, loading } = useAppSelector((s) => s.images);
  const { hasKey, storeKey } = useLocalKeys();

  const [recoverOpen, setRecoverOpen] = useState(false);
  const [recoverImageId, setRecoverImageId] = useState("");
  const [manualKey, setManualKey] = useState("");

  useEffect(() => {
    dispatch(fetchImages());
  }, [dispatch]);

  const quarantined = items.filter((img) => !hasKey(img.id));

  const handleRecover = useCallback(async () => {
    if (!recoverImageId || !manualKey.trim()) return;
    try {
      await storeKey(recoverImageId, manualKey.trim());
      notify(
        t("quarantine.recovered", "Key stored. Image recovered."),
        "success",
      );
      setRecoverOpen(false);
      setManualKey("");
      setRecoverImageId("");
    } catch {
      notify(t("quarantine.recoverError", "Failed to store key"), "error");
    }
  }, [recoverImageId, manualKey, storeKey, notify, t]);

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Skeleton variant="text" width={200} height={40} />
        <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <WarningIcon color="warning" />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
          }}
        >
          {t("quarantine.title", "Quarantine")}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 3,
        }}
      >
        {t(
          "quarantine.description",
          "Images without a matching encryption key in this browser. These images exist on the server but cannot be viewed.",
        )}
      </Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Chip
            label={t("quarantine.totalImages", "Total images: {{count}}", {
              count: items.length,
            })}
            variant="outlined"
          />
          <Chip
            label={t("quarantine.quarantinedCount", "Quarantined: {{count}}", {
              count: quarantined.length,
            })}
            color="warning"
          />
          <Chip
            label={t("quarantine.accessibleCount", "Accessible: {{count}}", {
              count: items.length - quarantined.length,
            })}
            color="success"
          />
        </CardContent>
      </Card>
      {quarantined.length === 0 ? (
        <Alert severity="success">
          {t(
            "quarantine.allClear",
            "All images have matching keys. Nothing is quarantined.",
          )}
        </Alert>
      ) : (
        <Card>
          <List>
            {quarantined.map((img) => (
              <ListItem
                key={img.id}
                divider
                sx={{ cursor: "pointer" }}
                onClick={() => navigate(routes.image(img.id))}
              >
                <ListItemText
                  primary={img.name}
                  secondary={`${(img.size / 1024).toFixed(1)} KB - ${new Date(
                    img.createdAt,
                  ).toLocaleDateString()}`}
                />
                <ListItemSecondaryAction>
                  <Button
                    size="small"
                    startIcon={<KeyIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setRecoverImageId(img.id);
                      setRecoverOpen(true);
                    }}
                  >
                    {t("quarantine.enterKey", "Enter Key")}
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Card>
      )}
      <Dialog
        open={recoverOpen}
        onClose={() => setRecoverOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("quarantine.recoverTitle", "Recover Image")}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            {t(
              "quarantine.recoverHint",
              "Paste the encrypted unlock key for this image. You can find it in a previously exported key bundle.",
            )}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label={t("quarantine.unlockKey", "Encrypted Unlock Key")}
            value={manualKey}
            onChange={(e) => setManualKey(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecoverOpen(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleRecover}
            disabled={!manualKey.trim()}
          >
            {t("quarantine.recover", "Recover")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
